import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import FloatingActions from '@/pages/home/components/FloatingActions';

const facility = {
  slug: 'accessories',
  name: 'BTL Trims Accessories.',
  tagline: 'Complete Trims & Components Manufacturing',
  description: 'Dedicated garment accessories manufacturer producing zippers, buttons, labels, hang tags, elastics, drawstrings, and packaging materials — all under one roof for seamless apparel supply chain integration.',
  longDescription: 'Beaumonde Accessories Ltd. is the dedicated trims and components manufacturing arm of the Beaumonde Tex group. We produce the full spectrum of garment accessories — metal and plastic zippers, resin and metal buttons, woven and printed labels, hang tags, elastics, drawstrings, and packaging materials. By manufacturing accessories in-house, we eliminate third-party vendor dependencies, compress lead times, and give apparel brands a single accountable partner for every trim and component their garments need. With 15 production lines, 300+ skilled personnel, and a monthly output of 50 million units, we deliver precision, consistency, and volume.',
  heroImage: 'https://readdy.ai/api/search-image?query=Professional%20product%20photography%20of%20organized%20garment%20accessories%20and%20trims%20display%2C%20zippers%20buttons%20labels%20elastics%20arranged%20on%20clean%20white%20surface%2C%20bright%20modern%20workshop%20background%2C%20soft%20studio%20lighting%2C%20warm%20neutral%20tones%2C%20high%20quality%20commercial%20photography&width=1800&height=800&seq=accessories-hero-individual&orientation=landscape',
  capacityStats: [
    { value: '60,000', label: 'Sq. Ft. Facility', icon: 'ri-building-4-line' },
    { value: '300+', label: 'Skilled Workforce', icon: 'ri-team-line' },
    { value: '15', label: 'Production Lines', icon: 'ri-settings-line' },
    { value: '50M', label: 'Monthly Units', icon: 'ri-stack-line' },
    { value: '12', label: 'Injection Molding', icon: 'ri-contrast-drop-2-line' },
    { value: '8', label: 'Printing Presses', icon: 'ri-printer-line' },
  ],
  machines: [
    { name: 'Injection Molding Machines', qty: '12', spec: 'Haitian & Chen Hsong, 120-450T', desc: 'High-speed precision molding for resin buttons, snap fasteners, and plastic components.' },
    { name: 'Zipper Assembly Lines', qty: '4', spec: 'YKK-grade automated assembly', desc: 'Metal and plastic zipper production with auto-slider attachment and quality testing.' },
    { name: 'Label Printing Presses', qty: '8', spec: 'Heidelberg & Komori offset', desc: 'Multi-color woven label, printed label, and heat transfer label production.' },
    { name: 'Die-Casting Machines', qty: '3', spec: 'Buhler & Toshiba cold chamber', desc: 'Precision metal button and rivet production from zinc alloy and brass.' },
    { name: 'Packaging Lines', qty: '3', spec: 'Automated bag making & sealing', desc: 'Poly bag extrusion, printing, cutting, and sealing for all export packaging needs.' },
    { name: 'Laser Engraving', qty: '2', spec: 'Trotec & Epilog CO2 lasers', desc: 'Custom branding, embossing patterns, and precision engraving on buttons and accessories.' },
  ],
  gallery: [
    {
      src: 'https://readdy.ai/api/search-image?query=Zipper%20manufacturing%20workshop%20with%20precision%20assembly%20machinery%2C%20organized%20production%20line%2C%20bright%20clean%20industrial%20environment%2C%20professional%20manufacturing%20photography%2C%20warm%20neutral%20tones&width=700&height=500&seq=accessories-gallery-01&orientation=landscape',
      alt: 'Zipper Assembly Line',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Button%20injection%20molding%20workshop%20with%20organized%20workstations%2C%20rows%20of%20molding%20machines%2C%20clean%20industrial%20facility%2C%20bright%20lighting%2C%20professional%20product%20manufacturing%20photography&width=700&height=500&seq=accessories-gallery-02&orientation=landscape',
      alt: 'Button Molding Workshop',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Label%20printing%20facility%20with%20printing%20presses%20and%20organized%20sample%20displays%2C%20modern%20industrial%20environment%2C%20bright%20clean%20workspace%2C%20professional%20print%20industry%20photography&width=700&height=500&seq=accessories-gallery-03&orientation=landscape',
      alt: 'Label Printing Floor',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Quality%20control%20station%20with%20accessories%20samples%20arranged%20for%20inspection%2C%20calipers%20and%20testing%20equipment%2C%20organized%20clean%20workspace%2C%20professional%20product%20photography&width=700&height=500&seq=accessories-gallery-04&orientation=landscape',
      alt: 'Quality Control Lab',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Packaging%20material%20production%20line%20with%20poly%20bags%20and%20cartons%2C%20modern%20machinery%2C%20clean%20organized%20factory%20floor%2C%20bright%20industrial%20lighting%2C%20professional%20photography&width=700&height=500&seq=accessories-gallery-05&orientation=landscape',
      alt: 'Packaging Production',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Finished%20accessories%20warehouse%20with%20organized%20shelving%20of%20zippers%20buttons%20labels%20and%20packaging%2C%20systematic%20inventory%2C%20bright%20clean%20storage%20facility%2C%20professional%20industrial%20photography&width=700&height=500&seq=accessories-gallery-06&orientation=landscape',
      alt: 'Finished Goods Warehouse',
    },
  ],
  capabilities: [
    { icon: 'ri-links-line', title: 'Zippers', desc: 'Metal and plastic zippers in all gauges, lengths, and finishes — including YKK-grade quality.' },
    { icon: 'ri-checkbox-blank-circle-line', title: 'Buttons', desc: 'Resin, metal, horn, shell, and wood buttons with custom embossing and color matching.' },
    { icon: 'ri-price-tag-3-line', title: 'Labels', desc: 'Woven labels, printed labels, heat transfer labels, and care labels with brand-specific designs.' },
    { icon: 'ri-bill-line', title: 'Hang Tags', desc: 'Custom hang tags, price tickets, barcode stickers, and RFID tags with variable data printing.' },
    { icon: 'ri-ruler-line', title: 'Elastics', desc: 'Woven and knitted elastics, drawstrings, cords, and tapes in all widths and compositions.' },
    { icon: 'ri-shopping-bag-line', title: 'Packaging', desc: 'Poly bags, cartons, inner boxes, tissue paper, and retail packaging with custom branding.' },
    { icon: 'ri-brush-3-line', title: 'Embossing', desc: 'Foil stamping, embossing, debossing, and screen printing for premium brand presentation.' },
    { icon: 'ri-leaf-line', title: 'Sustainable', desc: 'OEKO-TEX certified, recycled polyester, organic cotton, and FSC-certified paper options.' },
  ],
};

export default function AccessoriesPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 },
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[500px] md:h-[600px] flex items-end overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${facility.heroImage})` }}></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/25"></div>
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 pb-14 md:pb-20">
          <button
            onClick={() => navigate('/sister-concerns')}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors duration-200 mb-5 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            All Sister Concerns
          </button>
          <span className="inline-block px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold font-heading mb-4">
            Accessories
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 leading-tight">
            {facility.name}
          </h1>
          <p className="text-lg md:text-xl text-white/80 font-heading font-medium mb-4">{facility.tagline}</p>
          <p className="max-w-[650px] text-sm md:text-base text-white/65 leading-relaxed">{facility.description}</p>
        </div>
      </section>

      {/* <section className="relative -mt-10 z-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="bg-white rounded-lg border border-[#E6EAF0] p-6 md:p-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {facility.capacityStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="w-9 h-9 mx-auto flex items-center justify-center rounded-full bg-accent-50 mb-2">
                  <i className={`${stat.icon} text-accent-500 text-base`}></i>
                </div>
                <div className="font-heading text-xl md:text-2xl font-bold text-foreground-900">{stat.value}</div>
                <div className="text-[10px] text-foreground-500 mt-1 leading-tight">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pt-16 pb-14 md:pt-24 md:pb-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            <div className="lg:w-3/5 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
              <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-accent-500 mb-4">About the Facility</span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6 leading-tight">
                Every Trim. Every Component. One Source.
              </h2>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base mb-5">{facility.longDescription}</p>
            </div>
            <div className="lg:w-2/5 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-200">
              <div className="bg-background-100 rounded-lg p-6 border border-[#E6EAF0]">
                <h3 className="font-heading text-sm font-semibold text-foreground-800 mb-4 flex items-center gap-2">
                  <i className="ri-settings-3-line text-accent-500"></i>
                  Machine & Equipment
                </h3>
                <div className="space-y-4">
                  {facility.machines.map((m) => (
                    <div key={m.name} className="border-b border-[#E6EAF0] pb-3 last:border-0 last:pb-0">
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <p className="text-sm font-semibold text-foreground-800">{m.name}</p>
                        <span className="text-xs font-bold text-accent-500 whitespace-nowrap">{m.qty} units</span>
                      </div>
                      <p className="text-[11px] text-foreground-500 mb-1">{m.spec}</p>
                      <p className="text-xs text-foreground-400 leading-snug">{m.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-accent-500 mb-4">Inside the Facility</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Photo Tour</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {facility.gallery.map((img, i) => (
              <div
                key={img.alt}
                className="rounded-lg overflow-hidden border border-[#E6EAF0] animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 group"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <img src={img.src} alt={img.alt} className="w-full h-[280px] object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="bg-white px-4 py-3">
                  <p className="text-sm font-medium text-foreground-700">{img.alt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-accent-500 mb-4">What We Make</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Product Capabilities</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {facility.capabilities.map((cap, i) => (
              <div
                key={cap.title}
                className="bg-background-100 rounded-lg p-6 border border-[#E6EAF0] animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 hover:border-accent-200"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 mb-4">
                  <i className={`${cap.icon} text-accent-500 text-lg`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-800 mb-2">{cap.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden bg-accent-500">
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">Need Trims for Your Production?</h2>
          <p className="text-white/80 max-w-[500px] mx-auto mb-8 text-sm md:text-base leading-relaxed">
            From zippers to packaging, we manufacture it all — quality guaranteed, delivered on time.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/#contact')}
              className="inline-flex items-center px-8 py-3.5 bg-white text-accent-600 text-sm font-semibold font-heading rounded-md hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
            >
              Send an Inquiry
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
            <button
              onClick={() => navigate('/sister-concerns')}
              className="inline-flex items-center px-8 py-3.5 bg-transparent border-2 border-white/30 text-white text-sm font-semibold font-heading rounded-md hover:bg-white/10 transition-colors duration-200 whitespace-nowrap"
            >
              All Sister Concerns
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </div>
  );
}